'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { HotelListRecord } from '@bip/api-client';
import { IMG } from '../../../imgPrefix';
import { useCurrency } from '../../CurrencyProvider';
import { resolveDisplayPrice } from '../../../hotels/config';
import { PromoPrice } from '../../../hotels/_components/PromoPrice';
import './NearbyHotelCard.css';

const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;

function HotelImage({ src, alt }: { src?: string; alt: string }) {
  const [currentSrc, setCurrentSrc] = useState(src || FALLBACK_IMAGE);

  useEffect(() => {
    setCurrentSrc(src || FALLBACK_IMAGE);
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (currentSrc !== FALLBACK_IMAGE) setCurrentSrc(FALLBACK_IMAGE);
      }}
      className="rnh-img"
    />
  );
}

export function NearbyHotelCard({ hotel, referenceName }: { hotel: HotelListRecord; referenceName?: string }) {
  const { formatPrice, convertPrice, currencyCode } = useCurrency();
  const imageUrl = hotel.primaryImage || hotel.images?.[0];
  const hasPrice = hotel.totalPriceInUserCurrency != null || hotel.finalRate?.totalPriceWithCommission != null;
  const { total: totalConverted, promo: promoConverted, hasPromo } = hasPrice
    ? resolveDisplayPrice(hotel.finalRate, hotel.totalPriceInUserCurrency, hotel.userCurrency, convertPrice, currencyCode)
    : { total: 0, promo: undefined, hasPromo: false };
  const location = [hotel.city, hotel.state].filter(Boolean).join(', ');
  const dist = hotel.distanceFromGeo;
  const stars = Math.min(Math.floor(hotel.starRating || 0), 5);

  return (
    <div className="rnh-card">
      <div className="rnh-img-wrap">
        <HotelImage src={imageUrl} alt={hotel.name} />
      </div>

      <div className="rnh-body">
        <div className="rnh-name">{hotel.name}</div>

        {stars > 0 && (
          <div className="rnh-rating">
            <span className="rnh-rating__stars">
              {'★'.repeat(stars)}
              {'☆'.repeat(5 - stars)}
            </span>
          </div>
        )}

        {(location || dist != null) && (
          <div className="rnh-location">
            <i className="text-lg fal fa-map-marker-alt rnh-location__icon" />
            <span>
              {location}
              {dist != null && (
                <>, {dist.toFixed(2)} miles {referenceName ? ` from ${referenceName}` : ''}</>
              )}
            </span>
          </div>
        )}

        {hasPrice && (
          <div className="rnh-price">
            <span className="rnh-price__label">From </span>
            <PromoPrice
              regular={formatPrice(totalConverted, currencyCode)}
              promo={hasPromo ? formatPrice(promoConverted!, currencyCode) : undefined}
              size="text-lg"
            />
          </div>
        )}
      </div>

      <div className="rnh-book-wrap">
        <Link
          href={hotel.detailPageUrl || hotel.pageUrl || '/hotels'}
          className="rnh-book"
          target="_blank"
          rel="noopener noreferrer"
        >
          Book now
        </Link>
      </div>
    </div>
  );
}
